import { createContext, use } from "react";

import type { TaskResponse } from "api/model";
import type { ImageCornerCoordinates } from "context/CogOrtho/orthoAssets";

import type { OrthoUploadSession, OrthoUploadStatus } from "./orthoUpload";
import type {
  ExistingOrthoMeta,
  OrthoWorkflow,
  OrthoWorkflowKey,
} from "./orthoWorkflow";
import type {
  OrthoWorkflowActionName,
  OrthoWorkflowActions,
} from "./useOrthoWorkflowActions";

/** Acts on the upload dialog's active session, not on a particular workflow. */
export interface OrthoWorkflowUploadContext {
  /** The session currently shown in the upload dialog, if any. */
  activeSession: OrthoUploadSession | null;
  /** Whether any session is hashing or uploading (one Uppy instance is shared). */
  isAnyUploadInProgress: boolean;
  /** Stages files for upload on the active session, surfacing any staging error via its `errorMessage`. */
  addFiles: (files: File[]) => void;
  analyzeFolder: (files: File[]) => Promise<void>;
  /** Deactivates (and, if idle/errored, discards) the active session — call when the upload dialog closes. */
  clearActiveSession: () => void;
  resumeFolder: (tasks: TaskResponse[], files: File[]) => Promise<void>;
  /** Sets/clears the active session's `errorMessage` directly, for errors that don't go through `analyzeFolder`/`upload` (e.g. dropzone file rejections). */
  setErrorMessage: (message: string | null) => void;
  setUploadStatus: (status: OrthoUploadStatus) => void;
  setSelectedSiteId: (siteId: number | null) => void;
  setFlightDateTime: (flightDateTime: Date | null) => void;
  /** Starts a fresh session for a new upload and makes it active. A preset continues an existing ortho workflow. */
  startNewSession: (preset?: { orthoId: number; siteId: number }) => void;
  /** Starts a session pre-populated with `tasks` to resume, and makes it active. */
  startResumeSession: (tasks: TaskResponse[]) => void;
  /** Uploads the active session's currently selected/analyzed ortho folder to the current location. */
  upload: () => Promise<void>;
}

/**
 * Metadata extracted from existing orthos' COG headers, cached by ortho id
 * (stable across ortho show/hide). Reset when the location changes.
 */
export interface OrthoWorkflowMetadataContext {
  knownOrthoMeta: Readonly<Record<number, ExistingOrthoMeta>>;
  /**
   * Reads the COG header at `url` and caches all of its metadata. Loads at most
   * once per ortho (concurrent and repeat calls share one read); a failed read
   * is logged and may be retried by calling again.
   */
  loadOrthoMeta: (orthoId: number, url: string) => Promise<void>;
  /** Caches a footprint already known from elsewhere (e.g. a loaded map overlay) without a header read. */
  rememberOrthoBoundary: (
    orthoId: number,
    coordinates: ImageCornerCoordinates,
  ) => void;
}

export interface OrthoWorkflowContextValue {
  workflows: OrthoWorkflow[];
  byKey: ReadonlyMap<OrthoWorkflowKey, OrthoWorkflow>;
  /** True until the upload and creation tasks have loaded. */
  isPending: boolean;
  locationUuid: string;

  upload: OrthoWorkflowUploadContext;
  metadata: OrthoWorkflowMetadataContext;
  /** Per-workflow mutations, addressed by workflow key. */
  actions: OrthoWorkflowActions;
  /** Whether `action` is in flight for the workflow with `key`. */
  isActionPending: (
    action: OrthoWorkflowActionName,
    key: OrthoWorkflowKey,
  ) => boolean;
}

export const OrthoWorkflowContext =
  createContext<OrthoWorkflowContextValue | null>(null);

export const useOrthoWorkflows = (): OrthoWorkflowContextValue => {
  const context = use(OrthoWorkflowContext);
  if (context == null) {
    throw new Error(
      "useOrthoWorkflows must be used within OrthoWorkflowProvider",
    );
  }
  return context;
};
